import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';

import { theme } from '_constants/theme';
import { ROUTES } from '_constants/routes';
import { hexToRGBA } from '_helpers/theme';
import { createQueryString } from '_helpers/urls';

import {
  getBlogSubRoute,
  getBlogImageAlt,
  getBlogArticle,
} from '_helpers/blog';

import { refreshForm } from '_redux/order/slice';

import * as Layout from '_components/atomic/layouts';
import Breadcrumbs from '_components/atomic/Breadcrumbs';
import CheckAvailabilityForm from './CheckAvailabilityForm';

const StyledOuterContainer = styled.div`
  display: flex;
  justify-content: center;

  width: 100%;
  background: ${hexToRGBA(theme.colors.indigo[600], 0.05)};
`;

const StyledInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  max-width: ${theme.spaces.large24X * 10 + 80}px;
  width: 100%;

  padding-top: ${theme.spaces.large}px;
  padding-bottom: ${theme.spaces.large16X}px;

  @media ${theme.devices.tablet} {
    padding-bottom: ${theme.spaces.large6X}px;
    padding-left: ${theme.spaces.regular}px;
    padding-right: ${theme.spaces.regular}px;
  }
`;

const StyledRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${theme.spaces.large20X}px;
  width: 100%;

  @media ${theme.devices.tablet} {
    flex-direction: column;
    gap: ${theme.spaces.regular}px;
  }
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StyledImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media ${theme.devices.tablet} {
    img {
      width: 100%;
    }
  }
`;

const BlogHeaderSection = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const route = getBlogSubRoute(router.pathname);

  const {
    title = null,
    description = null,
    imgSrc = null,
  } = getBlogArticle(route);

  const imgAlt = getBlogImageAlt(route);

  const handleSubmit = (values) => {
    if (values) {
      dispatch(refreshForm({ ...values, step: 2, maxStep: 2 }));

      router.push(
        `${ROUTES.llcIncorporationFiling}${createQueryString({
          step: 2,
        })}`,
        undefined,
        { shallow: true }
      );
    }
  };

  return (
    <StyledOuterContainer>
      <StyledInnerContainer>
        <Breadcrumbs
          items={[
            { label: 'Home', path: ROUTES.home },
            { label: 'Blog', path: ROUTES.blogGallery },
            { label: title, path: `${ROUTES.blog}${route}` },
          ]}
        />
        <Layout.Divider desktop size={theme.spaces.large4X} />
        <Layout.Divider mobile size={theme.spaces.large} />
        <StyledRow>
          <StyledColumn>
            <Layout.Card.Text desktop size={theme.fontSizes.large4X} boldest>
              {title}
            </Layout.Card.Text>
            <Layout.Card.Text mobile size={theme.fontSizes.largeX} boldest>
              {title}
            </Layout.Card.Text>
            <Layout.Card.Text desktop size={theme.fontSizes.regular}>
              {description}
            </Layout.Card.Text>
            <Layout.Card.Text mobile size={theme.fontSizes.small}>
              {description}
            </Layout.Card.Text>
            <CheckAvailabilityForm onSubmit={handleSubmit} />
          </StyledColumn>
          <StyledColumn>
            <StyledImageContainer>
              <picture>
                <source srcSet={imgSrc} type="image/svg" />
                <img src={imgSrc} alt={imgAlt} />
              </picture>
            </StyledImageContainer>
          </StyledColumn>
        </StyledRow>
      </StyledInnerContainer>
    </StyledOuterContainer>
  );
};

export default BlogHeaderSection;
